import { PaperSize } from "@client-shared/types/pdfPrint";
import { Locale, PromotionTemplate, PurchaseOrder } from "@shared/types";
import { PDFDocument, StandardFonts } from "pdf-lib";
import { getDimensions, getScaledSpacing } from "./utils/dimensionUtils";
import {
  drawAddress,
  drawCustomerAccountDetails,
  drawCustomerAccountName,
  drawFooter,
  drawGiftMessage,
  drawHeader,
  drawOrderInfo,
  drawTableRow,
} from "./utils/drawingUtils";
import { getLocaleFromCountryCode, getVendorLocaleFromRegion } from "./utils/localeUtils";
import { drawPromotion } from "./utils/promotionUtils";
import { drawTableHeader } from "./utils/tableUtils";

export class PackingSlipGenerator {
  /**
   * Core logic for creating a packing slip PDF.
   * Shared between createPackingSlip and createPackingSlipInMemory to eliminate duplication.
   */
  private static async createPackingSlipCore(
    purchaseOrder: PurchaseOrder,
    size: PaperSize,
    promotionTemplate: PromotionTemplate | undefined,
    options:
      | {
          useDestinationLocale?: boolean;
          vendorRegion?: string;
          vendorLocale?: Locale;
          recipientLocale?: Locale;
          translateGrindType?: (value: string | undefined) => string | undefined;
          sltCustomerAccountNumber?: string;
          vendorName?: string;
        }
      | undefined
  ): Promise<PDFDocument> {
    // Determine locales
    const destinationCountry = purchaseOrder.shippingAddress?.countryCode ?? purchaseOrder.shippingAddress?.country ?? undefined;
    const impliedRecipientLocale: Locale = getLocaleFromCountryCode(destinationCountry);
    const impliedVendorLocale: Locale = options?.vendorLocale ?? getVendorLocaleFromRegion(options?.vendorRegion);

    const formatLocale: Locale = options?.useDestinationLocale ? impliedRecipientLocale : impliedVendorLocale;

    const dimensions = getDimensions(size);
    const pdfDoc = await PDFDocument.create();
    const page = pdfDoc.addPage([dimensions.width, dimensions.height]);

    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

    // Draw header
    let startY1 = dimensions.margin + getScaledSpacing(20, 10, dimensions);
    drawHeader(page, dimensions, boldFont, impliedVendorLocale, options?.recipientLocale ?? impliedRecipientLocale);

    // Draw promotion if available
    if (promotionTemplate) {
      const promotionLocale = options?.recipientLocale ?? impliedRecipientLocale;
      startY1 = await drawPromotion(promotionTemplate, page, dimensions, startY1, font, boldFont, promotionLocale);
    }

    // Draw customer account name
    const startY2 = await drawCustomerAccountName(page, purchaseOrder, dimensions, startY1, font);

    // Draw address and order info at the same Y position
    let startY = drawAddress(page, purchaseOrder, dimensions, startY2, font);

    // Draw order info at the same starting position as address
    drawOrderInfo({
      page,
      purchaseOrder,
      dimensions,
      startY: startY2,
      font,
      boldFont,
      vendorLocale: impliedVendorLocale,
      recipientLocale: options?.recipientLocale ?? impliedRecipientLocale,
      isEdiPartner: purchaseOrder.isEdiPartner,
      sltCustomerAccountNumber: options?.sltCustomerAccountNumber,
    });

    // Calculate proper Y position for table header
    const orderInfoHeight = getScaledSpacing(15, 10, dimensions);
    const tableStartY = purchaseOrder.shippingAddress ? startY : startY2 + orderInfoHeight;

    // Draw table header
    startY = drawTableHeader(page, {
      dimensions,
      startY: tableStartY,
      font,
      boldFont,
      vendorLocale: impliedVendorLocale,
      recipientLocale: options?.recipientLocale ?? impliedRecipientLocale,
    });

    // Draw table rows
    for (const lineItem of purchaseOrder.lineItems) {
      startY = drawTableRow(page, dimensions, startY, lineItem, font, formatLocale, options?.translateGrindType);
    }

    // Draw gift message
    startY = drawGiftMessage(page, purchaseOrder, dimensions, startY + getScaledSpacing(10, 5, dimensions), font);

    // Draw customer account details (notes)
    drawCustomerAccountDetails(page, purchaseOrder, dimensions, startY + getScaledSpacing(5, 2.5, dimensions), font);

    // Draw footer
    drawFooter(page, dimensions, font, options?.recipientLocale ?? impliedRecipientLocale, options?.vendorName);

    return pdfDoc;
  }

  static async createPackingSlip(
    purchaseOrder: PurchaseOrder,
    size: PaperSize = "A4",
    promotionTemplate?: PromotionTemplate,
    options?: {
      useDestinationLocale?: boolean;
      vendorRegion?: string;
      vendorLocale?: Locale;
      recipientLocale?: Locale;
      translateGrindType?: (value: string | undefined) => string | undefined;
      sltCustomerAccountNumber?: string;
      vendorName?: string;
    }
  ): Promise<string> {
    try {
      const pdfDoc = await PackingSlipGenerator.createPackingSlipCore(purchaseOrder, size, promotionTemplate, options);
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([new Uint8Array(pdfBytes)], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      return url;
    } catch (error) {
      console.error("Error creating packing slip:", error);
      throw error;
    }
  }

  /**
   * Creates a packing slip PDF document in memory (no blob URL).
   * Optimized for streaming operations to avoid intermediate blob URLs.
   *
   * @param purchaseOrder - The purchase order to generate a packing slip for
   * @param size - Paper size for the packing slip
   * @param promotionTemplate - Optional promotion template
   * @param options - Configuration options
   * @returns PDFDocument instance (not a blob URL)
   */
  static async createPackingSlipInMemory(
    purchaseOrder: PurchaseOrder,
    size: PaperSize = "A4",
    promotionTemplate?: PromotionTemplate,
    options?: {
      useDestinationLocale?: boolean;
      vendorRegion?: string;
      vendorLocale?: Locale;
      recipientLocale?: Locale;
      translateGrindType?: (value: string | undefined) => string | undefined;
      sltCustomerAccountNumber?: string;
      vendorName?: string;
    }
  ): Promise<PDFDocument> {
    return PackingSlipGenerator.createPackingSlipCore(purchaseOrder, size, promotionTemplate, options);
  }
}

// Re-export types and utilities that may be used externally
export { getDimensions } from "./utils/dimensionUtils";
export type { Dimensions } from "./utils/dimensionUtils";
export { getLocaleFromCountryCode } from "./utils/localeUtils";
export { clearBeanzLogoCache } from "./utils/logoUtils";
